import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  ArrowUpRight,
  Bookmark,
  BookmarkCheck,
  Share2,
  MessageCircleMore,
  Plus,
  Minus,
  Play, // Added for Music
  BookOpen, // Added for Books
} from "lucide-react";
import SingleProductLoader from "../../components/SingleProductLoader/SingleProductLoader";
import StarRating from "../../components/StarRating/StarRating";
import "./Product.css";
import {
  addItem,
  incrementQuantity,
  decrementQuantity,
  removeItem,
} from "../../redux/slices/cart";
import { useGetProduct } from "../../hooks/useGetProduct";
import { useFavorites } from "../../hooks/useFavorites";
import { toast } from "react-toastify";

function Product() {
  const token = useSelector((state) => state.auth.token);
  const { slug } = useParams();
  const { user } = useSelector((state) => state.auth);
  const cartItems = useSelector((state) => state.cart.items);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // --- DATA FETCHING ---
  const {
    data: product,
    isLoading,
    error,
  } = useGetProduct(slug, user?.id, token);

  const { addFavoriteMutation, removeFavoriteMutation } = useFavorites(token);

  const [isFavorite, setIsFavorite] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (product && product.favorite) {
      setIsFavorite(true);
    }
  }, [product]);

  // --- ACCESS LOGIC ---
  const hasAccess = product?.meta_data?.some(
    (meta) => meta.key === "_ebook_stream_url" && !!meta.value,
  );

  // --- CATEGORY CHECK ---
  // Checks if any category name contains "Book" (handles "Book", "Books", "Audiobook")
  const isBook = product?.categories?.some((cat) =>
    cat.name.toLowerCase().includes("book"),
  );

  // --- DEBOUNCED FAVORITES ---
  const debouncedFavorite = (actionFn) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      actionFn();
      debounceRef.current = null;
    }, 500);
  };

  const addToFavoriteHandler = () => {
    if (!token) {
      navigate("/login");
      return;
    }
    setIsFavorite(true);
    debouncedFavorite(() => addFavoriteMutation.mutate(product.id));
  };

  const removeFromFavoriteHandler = () => {
    if (!token) return;
    setIsFavorite(false);
    debouncedFavorite(() => removeFavoriteMutation.mutate(product.id));
  };

  // --- CART HANDLERS ---
  const itemInCart = cartItems.find((cartItem) => cartItem.id === product?.id);
  const itemQuantity = itemInCart?.quantity || 0;

  const addToCartHandler = ({ id, name, image, price, authorStripeId }) => {
    dispatch(addItem({ id, name, image, price, authorStripeId }));
  };

  const handleIncrement = (id) => {
    dispatch(incrementQuantity(id));
  };

  const handleDecrement = (id) => {
    if (itemInCart?.quantity === 1) {
      dispatch(removeItem(id));
    } else {
      dispatch(decrementQuantity(id));
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  if (isLoading)
    return (
      <section className="Product">
        <div className="loading-container">
          <SingleProductLoader />
        </div>
      </section>
    );

  if (error || !product)
    return (
      <section className="Product">
        <div className="error-container">
          <h2>{error ? "Oops! Something went wrong" : "Product Not Found"}</h2>
          <p>We couldn't find the product you're looking for.</p>
          <Link to="/" className="back-link">
            ← Back to Products
          </Link>
        </div>
      </section>
    );

  const item = product;

  return (
    <section className="Product">
      <div className="product-info">
        <img src={item.images?.[0]?.src} alt={item.name} />

        <div className="product-name">
          <div className="product-title">
            <h1 className="name">{item.name}</h1>
            <h1 className="price">${item.regular_price}</h1>
          </div>

          <p className="product-author">
            {item.meta_data?.find((meta) => meta.key === "author")?.value ||
              "Unknown Author"}
          </p>

          <StarRating rating={item.average_rating} />

          <p
            className="short-product-description"
            dangerouslySetInnerHTML={{ __html: item.short_description }}
          />

          <div className="product-action-buttons">
            {/* --- PURCHASED STATE --- */}
            {hasAccess ? (
              <button onClick={() => navigate("stream")}>
                {isBook ? (
                  <>
                    Read now <BookOpen size={18} />
                  </>
                ) : (
                  <>
                    Play now <Play size={18} />
                  </>
                )}
              </button>
            ) : itemInCart ? (
              /* --- IN CART STATE --- */
              <div className="product-quantity-controls">
                <button onClick={() => handleDecrement(item.id)}>
                  <Minus size={18} />
                </button>
                <span className="product-quantity-value">{itemQuantity}</span>
                <button onClick={() => dispatch(incrementQuantity(item.id))}>
                  <Plus size={18} />
                </button>
              </div>
            ) : (
              /* --- DEFAULT STATE --- */
              <button
                className="add-to-cart-btn"
                onClick={() => {
                  const authorStripeMeta = item.meta_data?.find(
                    (m) => m.key === "author_stripe_id",
                  );
                  const authorStripeId = authorStripeMeta
                    ? authorStripeMeta.value
                    : null;

                  toast.success("Added to cart!");

                  addToCartHandler({
                    id: item.id,
                    name: item.name,
                    image: item.images?.[0]?.src,
                    price: item.regular_price,
                    authorStripeId,
                  });
                }}
              >
                <ShoppingCart size={18} /> Add to cart
              </button>
            )}

            <ul className="product-utility-icons">
              <li>
                <button className="product-icon-button">
                  {isFavorite ? (
                    <BookmarkCheck
                      size={24}
                      color="white"
                      onClick={removeFromFavoriteHandler}
                    />
                  ) : (
                    <Bookmark
                      size={24}
                      color="white"
                      onClick={addToFavoriteHandler}
                    />
                  )}
                </button>
              </li>
              <li className="relative">
                <button onClick={handleShare} className="product-icon-button">
                  <Share2 size={24} color="white" />
                </button>
                {copied && <span className="tooltip">Copied!</span>}
              </li>
              <li>
                <Link to="reviews" className="product-icon-button">
                  <MessageCircleMore size={24} color="white" />
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="product-meta">
        <div className="description">
          <h3>Description</h3>
          <div
            className="product-description"
            dangerouslySetInnerHTML={{ __html: item.description }}
          />
        </div>
        <div className="meta">
          <h3>Author</h3>
          <p>
            {item.meta_data?.find((m) => m.key === "author")?.value || "N/A"}
          </p>
          <h3>Editor</h3>
          <p>
            {item.meta_data?.find((m) => m.key === "editor")?.value || "N/A"}
          </p>
          <h3>Genre</h3>
          <p>
            {item.meta_data?.find((m) => m.key === "genre")?.value || "N/A"}
          </p>
        </div>
      </div>
    </section>
  );
}

export default Product;
